"use client"

import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { useEffect } from "react"
import { Button } from "@/shared/components/ui/button"
import { ErrorAlertIcon } from "@/shared/components/icons/error-alert-icon"
import { reloadStaleDeploymentManually } from "@/shared/lib/stale-deployment"
import { useStaleDeploymentRecovery } from "@/shared/hooks/use-stale-deployment-recovery"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const { isStale, showReloadCta } = useStaleDeploymentRecovery(error)

  useEffect(() => {
    captureClientException(error, {
      scope: "client-render",
      extra: { digest: error.digest, boundary: "app" },
    })
  }, [error])

  const title = isStale ? "App updated" : "Something went wrong in the app"
  const description = showReloadCta
    ? "A new version is available. Reload to continue."
    : isStale
      ? "Reloading to get the latest version…"
      : error.message || "An unexpected error occurred. Please try again."
  const showActions = !isStale || showReloadCta

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
      <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
        <ErrorAlertIcon className="size-8 text-destructive" />
      </div>

      <h1 className="mt-6 text-2xl font-semibold text-foreground">
        {title}
      </h1>

      <p className="mt-2 max-w-md text-center text-muted-foreground">
        {description}
      </p>

      {error.digest && !isStale && (
        <p className="mt-1 text-xs text-muted-foreground">
          Error ID: {error.digest}
        </p>
      )}

      {showActions && (
        <div className="mt-8 flex items-center gap-4">
          {showReloadCta ? (
            <Button onClick={reloadStaleDeploymentManually}>Reload</Button>
          ) : (
            <Button onClick={reset}>Try Again</Button>
          )}
          <Button variant="outline" asChild>
            <LocalizedLink href="/">Go Home</LocalizedLink>
          </Button>
        </div>
      )}
    </div>
  )
}
